import {WallPiece, WallPieceRotation} from './WallPiece';
import {Loc, Location} from './Location';
import {CanBuildAtFn, GameModeName} from '../modes/GameMode';

const invalidBuildDistance = 999;
const maxBuildDistance = 30;

// types

export interface Spawn {
    x: number;
    y: number;
    walls: Loc[];
    owner: PlayerIndex;
    held: boolean;
    prevHeldBy: PlayerIndex;
}

interface Projectile {
    id: number;
    target: Loc;
    startAt: number;
}

export interface Cannon {
    x: number;
    y: number;
    owner: PlayerIndex;
    projectile?: Projectile;
}

export interface Wall {
    damaged: boolean;
    owner: PlayerIndex;
}

type PlayerIndex = number;

export type TerrainKind = 'water' | 'land';

export interface Terrain {
    kind: TerrainKind;
    owner: PlayerIndex | null;
}

interface State {
    modeName: GameModeName;
    width: number;
    height: number;
    playerCount: number;
    terrain: Terrain[][];
    spawns: Spawn[];
    cannons: Cannon[];
    heldBy: PlayerIndex[][];
    walls: (Wall | null)[][];
}

type IEntity = Wall | Cannon | Spawn | null;

interface ClientMapState {
    modeName: GameModeName;
    width: number;
    height: number;
    playerCount: number;
    terrain: Terrain[][];
    spawns: Spawn[];
    cannons: Cannon[];
    heldBy: PlayerIndex[][];
    walls: (Wall | null)[][];
}

export interface SetupMapState {
    modeName: GameModeName;
    width: number;
    height: number;
    playerCount: number;
    terrain: Terrain[][];
    spawns: Spawn[];
    cannons: Cannon[];
    walls: (Wall | null)[][];
}

// helpers

export function createGrid<T>(width: number, height: number, valueFn: (loc: Location) => T): T[][] {
    const grid: T[][] = [];
    for (let y = 0; y < height; y++) {
        grid[y] = [];
        for (let x = 0; x < width; x++) {
            grid[y].push(valueFn(new Location(x, y)));
        }
    }
    return grid;
}

// map state

export class MapState implements State {
    public modeName: GameModeName;
    public width: number;
    public height: number;
    public playerCount: number;
    public terrain: Terrain[][];
    public spawns: Spawn[] = [];
    public cannons: Cannon[];
    public heldBy: PlayerIndex[][];
    public walls: (Wall | null)[][];
    public isDirty: boolean;
    public entities?: IEntity[][];

    public static fromState(state: State | ClientMapState): MapState {
        const map = new MapState();
        map.modeName = state.modeName;
        map.width = state.width;
        map.height = state.height;
        map.playerCount = state.playerCount;
        map.terrain = createGrid(state.width, state.height, loc => state.terrain[loc.y][loc.x]);
        map.spawns = state.spawns.map(spawn => ({
            ...spawn,
            walls: [...spawn.walls.map(loc => ({x: loc.x, y: loc.y}))],
        }));
        map.cannons = state.cannons.map(cannon => ({...cannon}));
        map.heldBy = createGrid(state.width, state.height, loc => state.heldBy[loc.y][loc.x]);
        map.walls = createGrid(state.width, state.height, loc =>
            state.walls[loc.y][loc.x]
                ? // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
                  ({...state.walls[loc.y][loc.x]} as Wall)
                : null
        );
        map.isDirty = true;
        return map;
    }

    public static fromSetupState(state: SetupMapState): MapState {
        const map = new MapState();
        map.modeName = state.modeName;
        map.width = state.width;
        map.height = state.height;
        map.playerCount = state.playerCount;
        map.terrain = createGrid(state.width, state.height, loc => state.terrain[loc.y][loc.x]);
        map.spawns = state.spawns.map(spawn => ({
            ...spawn,
            walls: [...spawn.walls.map(loc => ({x: loc.x, y: loc.y}))],
        }));
        map.cannons = [...state.cannons.map(({x, y}) => ({x, y, owner: -1}))];
        map.heldBy = createGrid(state.width, state.height, _loc => -1);
        map.walls = createGrid(state.width, state.height, loc =>
            state.walls[loc.y][loc.x]
                ? // eslint-disable-next-line @typescript-eslint/no-object-literal-type-assertion
                  ({...state.walls[loc.y][loc.x]} as Wall)
                : null
        );
        map.isDirty = true;
        return map;
    }

    // countHeldTiles(): Map<number, number> {
    //     this.updateHeld();
    //     const heldCount = new Map<number, number>();
    //     for (let y = 0; y < this.height; y++) {
    //         for (let x = 0; x < this.width; x++) {
    //             const owner = this.heldBy[y][x];
    //             const count = heldCount.get(owner) || 0;
    //             heldCount.set(owner, count + 1);
    //         }
    //     }
    //     return heldCount;
    // }

    public forEachTile(callback: (loc: Location) => void): void {
        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                callback(new Location(x, y));
            }
        }
    }

    public forEachEdgeTile(callback: (loc: Location) => void): void {
        for (let y = 0; y < this.height; y++) {
            callback(new Location(0, y));
            callback(new Location(this.width - 1, y));
        }
        for (let x = 1; x < this.width - 1; x++) {
            callback(new Location(x, 0));
            callback(new Location(x, this.height - 1));
        }
    }

    public countHeldSpawns(): Map<number, number> {
        this.updateHeld();
        const heldCount = new Map<number, number>();
        this.spawns
            .filter(spawn => spawn.held)
            .forEach(spawn => {
                const owner = this.heldBy[spawn.y][spawn.x];
                const count = heldCount.get(owner) || 0;
                heldCount.set(owner, count + 1);
            });
        return heldCount;
    }

    public placeWalls(playerIndex: number, locations: Loc[]): void {
        locations.forEach(loc => {
            this.walls[loc.y][loc.x] = {damaged: false, owner: playerIndex};
        });
        this.isDirty = true;
        this.updateHeld();
    }

    public placeCannon(owner: number, x: number, y: number): void {
        const cannon = {x, y, owner};
        this.cannons.push(cannon);
        this.isDirty = true;
    }

    public removeCannon(x: number, y: number): void {
        this.cannons = this.cannons.filter(cannon => cannon.x !== x || cannon.y !== y);
        this.isDirty = true;
    }

    public canPlayerPlaceCannon(playerIndex: number, x: number, y: number): boolean {
        return (
            this.locationsForCannonAt(new Location(x, y))
                .filter(loc => this.isValidLocation(loc))
                .filter(loc => this.isEmpty(loc))
                .filter(loc => this.isOwnerAt(playerIndex, loc)).length === 4
        );
    }

    public canBuildWallPiece(
        playerIndex: number,
        x: number,
        y: number,
        rotation: WallPieceRotation,
        tiles: Loc[],
        canBuildAt: CanBuildAtFn
    ): boolean {
        const piece = new WallPiece(tiles, rotation);
        const locs = piece.rotatedLocations(new Location(x, y));
        return (
            locs.every(loc => this.isValidLocation(loc) && this.isEmpty(loc)) &&
            canBuildAt(this, locs, playerIndex)
        );
    }

    public placeWallPiece(
        playerIndex: number,
        x: number,
        y: number,
        rotation: WallPieceRotation,
        tiles: Loc[]
    ): void {
        const piece = new WallPiece(tiles, rotation);
        const locations = piece.rotatedLocations(new Location(x, y));
        this.placeWalls(playerIndex, locations);
    }

    // helpers

    private locationsForCannonAt(loc: Location): Location[] {
        return [loc, loc.offset(1, 0), loc.offset(0, 1), loc.offset(1, 1)];
    }

    private locationsForSpawnAt(loc: Location): Location[] {
        return [loc, loc.offset(1, 0), loc.offset(0, 1), loc.offset(1, 1)];
    }

    private isEmpty(loc: Location): boolean {
        const ent = this.getEntities();
        return !ent[loc.y][loc.x];
    }

    private isOwnerAt(playerIndex: number, loc: Location): boolean {
        return this.heldBy[loc.y][loc.x] === playerIndex;
    }

    // entity-related

    public getEntities(): IEntity[][] {
        if (this.isDirty || !this.entities) {
            this.entities = createGrid(
                this.width,
                this.height,
                loc => this.walls[loc.y][loc.x] || null
            );
            this.spawns.forEach(spawn => {
                this.locationsForSpawnAt(new Location(spawn.x, spawn.y)).forEach(loc => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    this.entities![loc.y][loc.x] = spawn;
                });
            });
            this.cannons.forEach(cannon => {
                this.locationsForCannonAt(new Location(cannon.x, cannon.y)).forEach(loc => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    this.entities![loc.y][loc.x] = cannon;
                });
            });
            this.isDirty = false;
        }
        return this.entities;
    }

    // held

    public isValidLocation(loc: Location): boolean {
        return 0 <= loc.x && loc.x < this.width && 0 <= loc.y && loc.y < this.height;
    }

    public isLand(loc: Location): boolean {
        return this.terrain[loc.y][loc.x].kind === 'land';
    }

    public isWall(loc: Location): boolean {
        return this.isValidLocation(loc) && !!this.walls[loc.y][loc.x];
    }

    public isPlayerWall(loc: Location, owner: number): boolean {
        return (
            this.isValidLocation(loc) &&
            Boolean(this.walls[loc.y][loc.x]) &&
            this.walls[loc.y][loc.x]!.owner === owner
        );
    }

    public updateHeld(): void {
        this.heldBy = createGrid(this.width, this.height, () => -1);
        for (let owner = 0; owner < this.playerCount; owner++) {
            const isInvalid: boolean[][] = createGrid(this.width, this.height, () => false);
            const visited: boolean[][] = createGrid(this.width, this.height, () => false);
            this.forEachEdgeTile(loc => this.searchHeld(loc, owner, isInvalid, visited));
            this.forEachTile(loc => {
                if (!isInvalid[loc.y][loc.x] && !this.isWall(loc)) {
                    this.heldBy[loc.y][loc.x] = owner;
                }
            });
        }
        this.spawns.forEach(spawn => {
            spawn.held = this.heldBy[spawn.y][spawn.x] !== -1;
        });
    }

    private searchHeld(
        loc: Location,
        owner: number,
        isInvalid: boolean[][],
        visited: boolean[][]
    ): void {
        const isPlayerWall = (loc: Location): boolean =>
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.isWall(loc) && this.walls[loc.y][loc.x]!.owner === owner;
        if (isPlayerWall(loc)) return;
        const queue: Location[] = [loc];
        visited[loc.y][loc.x] = true;

        while (queue.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const loc: Location = queue.pop()!;
            isInvalid[loc.y][loc.x] = true;

            this.valid8Neighbors(loc).forEach(loc => {
                if (visited[loc.y][loc.x]) return;
                visited[loc.y][loc.x] = true;
                if (isPlayerWall(loc)) {
                    isInvalid[loc.y][loc.x] = true;
                } else {
                    queue.push(loc);
                }
            });
        }
    }

    private valid8Neighbors(loc: Location): Location[] {
        return [[-1, 0], [1, 0], [0, -1], [0, 1], [-1, -1], [1, 1], [1, -1], [-1, 1]]
            .map(([x, y]) => loc.offset(x, y))
            .filter(loc => this.isValidLocation(loc));
    }

    public valid4Neighbors(loc: Location): Location[] {
        return [loc.offset(-1, 0), loc.offset(0, -1), loc.offset(1, 0), loc.offset(0, 1)].filter(
            loc => this.isValidLocation(loc)
        );
    }

    // to state

    public toState(): ClientMapState {
        return {
            modeName: this.modeName,
            width: this.width,
            height: this.height,
            playerCount: this.playerCount,
            terrain: this.terrain,
            spawns: this.spawns,
            cannons: this.cannons,
            heldBy: this.heldBy,
            walls: this.walls,
        };
    }

    public possibleCannonLocations(playerIndex: number): Loc[] {
        const possibilities: Loc[] = [];
        const isOk = (x: number, y: number): boolean =>
            this.isValidLocation(new Location(x, y)) &&
            this.heldBy[y][x] === playerIndex &&
            !this.getEntities()[y][x];
        this.forEachTile(({x, y}) => {
            if (isOk(x, y) && isOk(x + 1, y) && isOk(x, y + 1) && isOk(x + 1, y + 1)) {
                possibilities.push({x, y});
            }
        });
        return possibilities;
    }

    public heldSpawns(playerIndex: number): Spawn[] {
        return this.spawns.filter(spawn => this.heldBy[spawn.y][spawn.x] === playerIndex);
    }

    public unheldSpawns(): Spawn[] {
        return this.spawns.filter(spawn => !spawn.held);
    }

    // Returns spawns originally given to the player; does not reflect who currently holds a spawn
    public spawnsOwnedByPlayer(playerIndex: number): Spawn[] {
        return this.spawns.filter(spawn => spawn.owner === playerIndex);
    }

    public distanceGrid(sourceLocations: Location[]): number[][] {
        const grid: number[][] = createGrid(this.width, this.height, () => invalidBuildDistance);
        const queue: Location[] = [];
        sourceLocations.forEach(loc => {
            this.locationsForSpawnAt(loc).forEach(loc => {
                grid[loc.y][loc.x] = 0;
                queue.push(loc);
            });
        });
        while (queue.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            const origin: Location = queue.shift()!;
            const distance = grid[origin.y][origin.x];
            // eslint-disable-next-line no-loop-func
            this.valid4Neighbors(origin).forEach(loc => {
                if (!this.isLand(loc)) return;
                const curr = grid[loc.y][loc.x];
                if (distance + 1 < curr) {
                    grid[loc.y][loc.x] = distance + 1;
                    if (distance + 1 < maxBuildDistance) {
                        queue.push(loc);
                    }
                }
            });
        }
        return grid;
    }

    public clearDamagedWalls(): void {
        this.forEachTile(({x, y}) => {
            const wall = this.walls[y][x];
            if (wall && wall.damaged) {
                this.walls[y][x] = null;
            }
        });
    }
}



// WEBPACK FOOTER //
// ./src/common/map/MapState.ts